/**
 * Platform-admin User Tasklist ("Pendências") — the THIRD UI plane, beside the
 * lawyer-facing LexIDE pane + the concierge/IM surfaces.
 *
 * A platform admin picks a runtime/user (Fleet → Connect) and sees THAT
 * lawyer's pending-work list with its full ledger history, for support, fleet
 * monitoring, and debugging. Platform-admin already has full authorized fleet
 * access (same trust model as ExecutionLedger / OperationalLogs /
 * RuntimeFsBrowser), so this is monitoring visibility, NOT a privacy
 * expansion.
 *
 * READ-ONLY (v1) — no add/edit/note/archive affordances anywhere by design
 * (handoff §2.1). The runtime `tasklist.*` methods make admin mutation
 * trivially addable later if the operator wants it.
 *
 * Data path (handoff §3): REST through the platform proxy, NOT useGatewayRpc —
 * see `lib/tasklist-api.ts`. Ordering: the SERVER's list order is canonical;
 * this page only GROUPS (`@lexiun/tasklist-core`), never re-sorts. The types,
 * the comparator and the grouping are the SHARED package — reconciled to the
 * runtime and shared with the LexIDE pane, so no rule is duplicated here.
 *
 * Language note: LexPortal is an English-only platform-admin tool (no i18n),
 * so the section/labels are English here even though the lawyer-facing LexIDE
 * pane uses pt-BR ("Vencidas" / "Vence em breve"). Same semantics, staff
 * locale. Flagged in the handback.
 *
 * Deferred (handoff §2.1): the inspector-health strip — it reads the
 * executionLedger rows the task-inspector forwards, which only exist once
 * Part C ships.
 *
 * Handoff: ai-docs/handoffs/user-tasklist-lexportal-handoff-2026-07-21.md
 */

import {
  AlertTriangle,
  Bot,
  CalendarClock,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  Loader2,
  RefreshCw,
  User as UserIcon,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatRelative } from "@/components/uploads/upload-time";
import { useRuntimeContext } from "@/hooks/useRuntimeContext";
import { usePlatformApi } from "@/hooks/usePlatformApi";
import {
  getTaskDetail,
  listTasklist,
  TASK_NOT_FOUND,
  TasklistApiError,
} from "@/lib/tasklist-api";
import {
  groupTasks,
  isOverdueForDisplay,
  localDatePlus,
  localToday,
  type TaskDetailResult,
  type TaskPriority,
  type TaskRow,
  type TaskStatus,
  type TasklistFilters,
  type UpdateRow,
} from "@lexiun/tasklist-core";

const STATUSES: TaskStatus[] = ["open", "waiting", "done", "dropped"];
const PRIORITIES: TaskPriority[] = ["urgent", "high", "normal", "low"];

/** The ULID short-suffix shown beside a task id (handoff §2.1). A display
 *  affordance, not an ordering rule — so it stays local rather than in the
 *  shared package. */
function taskIdSuffix(id: string, len = 6): string {
  return id.length <= len ? id : id.slice(-len);
}
/** Ledger page size. The ledger is append-only + unbounded, so the detail pane
 *  always pages rather than fetching it whole; a long-running recurring task
 *  accumulates hundreds of rows. Kept small enough that the shared 12-update
 *  seed row exercises the "Load older" path (handoff §6). */
const HISTORY_PAGE = 10;

export function RuntimeTasklist() {
  const { target } = useRuntimeContext();
  const userId = target?.userId ?? null;
  const orgId = target?.orgId ?? null;
  const api = usePlatformApi();

  const [filters, setFilters] = useState<TasklistFilters>({});
  const [tasks, setTasks] = useState<TaskRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showTerminal, setShowTerminal] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  /** Valid caseIds for the runtime's org — lets an unresolvable `caseRef`
   *  render the "archived" badge (handoff §2.1). Best-effort + decorative:
   *  when the registry read fails we simply don't badge, never break the page. */
  const [validCaseIds, setValidCaseIds] = useState<Set<string> | null>(null);

  const [detail, setDetail] = useState<TaskDetailResult | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  // The viewer's LOCAL day. NOTE the two planes deliberately differ and this is
  // a KNOWN open item owned by the runtime lane, not an oversight here
  // (tasklist-core-lexportal-handoff §7): the gateway derives its reference day
  // with `todayYmdUtc`, so for anyone west of UTC — every pt-BR user — the
  // server's `due=overdue|soon` filter and this grouping disagree for the last
  // hours of the day. Do NOT "fix" it by switching to `todayYmdUtc`: that would
  // change the horizon the pane draws without touching the server's filter.
  const today = localToday();
  const soonCutoff = localDatePlus(2); // ~48h window

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const res = await listTasklist(userId, filters);
      setTasks(res.tasks);
      setError(null);
    } catch (e) {
      setError(
        e instanceof TasklistApiError
          ? `${e.code}: ${e.message}`
          : e instanceof Error
            ? e.message
            : "Failed to load tasklist",
      );
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, [userId, filters]);

  useEffect(() => {
    void load();
  }, [load]);

  // Case-id set for the archived/unknown-ref badge.
  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    api
      .listCases(orgId)
      .then((r) => {
        if (!cancelled) setValidCaseIds(new Set(r.data.map((c) => c.caseId)));
      })
      .catch(() => {
        if (!cancelled) setValidCaseIds(null);
      });
    return () => {
      cancelled = true;
    };
  }, [api, orgId]);

  // Detail (+ first history page) on selection.
  useEffect(() => {
    if (!userId || !selectedId) {
      setDetail(null);
      setDetailError(null);
      return;
    }
    let cancelled = false;
    // Drop the previous task's result FIRST. The header renders from the list
    // row (which switches instantly) while the ledger renders from `detail`;
    // keeping the old detail through the fetch would show one task's history
    // under another task's title — a wrong-record read in a support tool.
    setDetail(null);
    setDetailLoading(true);
    setDetailError(null);
    getTaskDetail(userId, selectedId, { historyLimit: HISTORY_PAGE })
      .then((r) => {
        if (!cancelled) setDetail(r);
      })
      .catch((e) => {
        if (cancelled) return;
        // The live route answers 404 for an unknown taskId — normal when a task
        // is archived/removed between the list read and the click, so it reads
        // as a plain statement rather than a transport failure.
        setDetailError(
          e instanceof TasklistApiError && e.code === TASK_NOT_FOUND
            ? "This task no longer exists — it may have been removed since the list was loaded."
            : e instanceof Error
              ? e.message
              : "Failed to load task",
        );
        setDetail(null);
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId, selectedId]);

  /** Append the next (older) ledger page — the ledger is append-only and
   *  unbounded, so the timeline is paginated newest-first. */
  const loadOlder = useCallback(async () => {
    if (!userId || !selectedId || !detail || detail.history.length === 0) return;
    const oldest = detail.history[detail.history.length - 1]!;
    setLoadingMore(true);
    try {
      const next = await getTaskDetail(userId, selectedId, {
        historyLimit: HISTORY_PAGE,
        historyBefore: oldest.at,
      });
      setDetail((prev) =>
        prev ? { ...prev, history: [...prev.history, ...next.history] } : next,
      );
    } catch {
      /* keep what's already shown — the "load older" button stays available */
    } finally {
      setLoadingMore(false);
    }
  }, [userId, selectedId, detail]);

  // The server's order is canonical — group only, never re-sort.
  const groups = useMemo(
    () => groupTasks(tasks, today, soonCutoff),
    [tasks, today, soonCutoff],
  );

  const selected = tasks.find((t) => t.id === selectedId) ?? detail?.task ?? null;

  if (!userId) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <ClipboardList className="h-8 w-8 text-slate-400" />
        <h1 className="mt-2 text-xl font-semibold">User Tasklist</h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Select a runtime from the fleet to view that user&apos;s pending work.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-3" data-testid="runtime-tasklist">
      <TasklistFilterBar
        value={filters}
        onChange={(f) => {
          setFilters(f);
          setSelectedId(null);
        }}
        onRefresh={() => void load()}
        loading={loading}
      />

      {error && (
        <div
          className="shrink-0 rounded-md bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400"
          data-testid="tasklist-error"
        >
          {error}
        </div>
      )}

      <div className="grid min-h-0 flex-1 grid-cols-[minmax(0,3fr)_minmax(0,2fr)] gap-4">
        {/* List */}
        <Card className="flex min-h-0 flex-col overflow-hidden">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <ClipboardList className="h-4 w-4" />
              {loading ? "Loading…" : `${tasks.length} task${tasks.length === 1 ? "" : "s"}`}
            </CardTitle>
          </CardHeader>
          <CardContent className="min-h-0 flex-1 overflow-y-auto p-2" data-testid="tasklist-list">
            {!loading && tasks.length === 0 && (
              <p className="py-10 text-center text-sm text-slate-400">
                No tasks for this user yet.
              </p>
            )}
            <TaskGroup
              title="Overdue"
              tone="danger"
              tasks={groups.overdue}
              {...{ selectedId, setSelectedId, today, validCaseIds }}
            />
            <TaskGroup
              title="Due soon"
              tone="warn"
              tasks={groups.soon}
              {...{ selectedId, setSelectedId, today, validCaseIds }}
            />
            <TaskGroup
              title={groups.overdue.length || groups.soon.length ? "Other open" : ""}
              tasks={groups.rest}
              {...{ selectedId, setSelectedId, today, validCaseIds }}
            />
            {groups.terminal.length > 0 && (
              <div className="mt-2">
                <button
                  type="button"
                  onClick={() => setShowTerminal((v) => !v)}
                  className="flex w-full items-center gap-1 rounded px-2 py-1 text-xs font-semibold uppercase tracking-wide text-slate-500 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-800"
                  data-testid="tasklist-terminal-toggle"
                >
                  {showTerminal ? (
                    <ChevronDown className="h-3 w-3" />
                  ) : (
                    <ChevronRight className="h-3 w-3" />
                  )}
                  Done / dropped ({groups.terminal.length})
                </button>
                {showTerminal && (
                  <TaskGroup
                    title=""
                    tasks={groups.terminal}
                    {...{ selectedId, setSelectedId, today, validCaseIds }}
                  />
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Detail */}
        <TaskDetailPane
          task={selected}
          detail={detail}
          loading={detailLoading}
          error={detailError}
          onLoadOlder={() => void loadOlder()}
          loadingMore={loadingMore}
          validCaseIds={validCaseIds}
          today={today}
        />
      </div>
    </div>
  );
}

// ─── Filters ────────────────────────────────────────────────────────────────

function TasklistFilterBar({
  value,
  onChange,
  onRefresh,
  loading,
}: {
  value: TasklistFilters;
  onChange: (f: TasklistFilters) => void;
  onRefresh: () => void;
  loading: boolean;
}) {
  const set = <K extends keyof TasklistFilters>(k: K, v: TasklistFilters[K]) =>
    onChange({ ...value, [k]: v });

  return (
    <div
      className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2 rounded-md border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-900"
      data-testid="tasklist-filters"
    >
      <div className="flex items-center gap-1">
        <span className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Status
        </span>
        {STATUSES.map((s) => {
          // `status` is a LIST in the frozen contract; this single-select chip
          // row sends a one-element array (same shape LexIDE sends).
          const active = value.status?.[0] === s;
          return (
            <button
              key={s}
              type="button"
              onClick={() => set("status", active ? undefined : [s])}
              className={`rounded border px-1.5 py-0.5 text-[11px] transition-colors ${
                active
                  ? "border-indigo-300 bg-indigo-50 text-indigo-700 dark:border-indigo-700 dark:bg-indigo-950 dark:text-indigo-300"
                  : "border-slate-200 text-slate-500 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800"
              }`}
              data-testid={`tasklist-filter-status-${s}`}
            >
              {s}
            </button>
          );
        })}
      </div>

      <label className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
        Priority
        <select
          value={value.priority?.[0] ?? ""}
          onChange={(e) =>
            set("priority", e.target.value ? [e.target.value as TaskPriority] : undefined)
          }
          className="rounded border border-slate-200 bg-white px-1 py-0.5 text-[11px] normal-case dark:border-slate-700 dark:bg-slate-950"
          data-testid="tasklist-filter-priority"
        >
          <option value="">any</option>
          {PRIORITIES.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
      </label>

      <label className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
        Due
        <select
          value={value.due ?? ""}
          onChange={(e) => set("due", (e.target.value || undefined) as "overdue" | "soon" | undefined)}
          className="rounded border border-slate-200 bg-white px-1 py-0.5 text-[11px] normal-case dark:border-slate-700 dark:bg-slate-950"
          data-testid="tasklist-filter-due"
        >
          <option value="">any</option>
          <option value="overdue">overdue</option>
          <option value="soon">due soon</option>
        </select>
      </label>

      <label className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
        Case
        <input
          type="text"
          placeholder="case-…"
          value={value.caseId ?? ""}
          onChange={(e) => set("caseId", e.target.value || undefined)}
          className="w-40 rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[11px] normal-case dark:border-slate-700 dark:bg-slate-950"
          data-testid="tasklist-filter-case"
        />
      </label>

      <label className="flex cursor-pointer items-center gap-1 text-[11px] text-slate-600 dark:text-slate-300">
        <input
          type="checkbox"
          checked={Boolean(value.includeArchived)}
          onChange={(e) => set("includeArchived", e.target.checked || undefined)}
          className="h-3 w-3"
          data-testid="tasklist-filter-archived"
        />
        Show archived
      </label>

      <button
        type="button"
        onClick={onRefresh}
        disabled={loading}
        className="ml-auto inline-flex items-center gap-1 rounded border border-slate-200 px-1.5 py-0.5 text-[11px] text-slate-500 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800"
        data-testid="tasklist-refresh"
      >
        <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> Refresh
      </button>
    </div>
  );
}

// ─── List group + row ───────────────────────────────────────────────────────

function TaskGroup({
  title,
  tone,
  tasks,
  selectedId,
  setSelectedId,
  today,
  validCaseIds,
}: {
  title: string;
  tone?: "danger" | "warn";
  tasks: TaskRow[];
  selectedId: string | null;
  setSelectedId: (id: string) => void;
  today: string;
  validCaseIds: Set<string> | null;
}) {
  if (tasks.length === 0) return null;
  const toneCls =
    tone === "danger"
      ? "text-rose-600 dark:text-rose-400"
      : tone === "warn"
        ? "text-amber-600 dark:text-amber-400"
        : "text-slate-500 dark:text-slate-400";
  return (
    <div className="mb-2">
      {title && (
        <p
          className={`px-2 py-1 text-xs font-semibold uppercase tracking-wide ${toneCls}`}
          data-testid={`tasklist-group-${title.toLowerCase().replace(/\s+/g, "-")}`}
        >
          {title} ({tasks.length})
        </p>
      )}
      {tasks.map((t) => (
        <TaskListRow
          key={t.id}
          task={t}
          selected={selectedId === t.id}
          onSelect={() => setSelectedId(t.id)}
          today={today}
          validCaseIds={validCaseIds}
        />
      ))}
    </div>
  );
}

function TaskListRow({
  task,
  selected,
  onSelect,
  today,
  validCaseIds,
}: {
  task: TaskRow;
  selected: boolean;
  onSelect: () => void;
  today: string;
  validCaseIds: Set<string> | null;
}) {
  const overdue = isOverdueForDisplay(task, today);
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`flex w-full flex-col gap-0.5 rounded-md border-b border-slate-100 px-2 py-1.5 text-left text-sm transition-colors dark:border-slate-800 ${
        selected
          ? "bg-indigo-50 dark:bg-indigo-950"
          : "hover:bg-slate-50 dark:hover:bg-slate-800"
      }`}
      data-testid={`tasklist-row-${task.id}`}
    >
      <div className="flex items-center gap-1.5">
        <PriorityMark priority={task.priority} />
        <span className="truncate font-medium">{task.title}</span>
        <StatusChip status={task.status} />
        {task.audit.archivedAt && <Badge tone="slate">archived</Badge>}
        {task.dueDate && (
          <span
            className={`ml-auto shrink-0 text-[11px] ${
              overdue ? "font-semibold text-rose-600 dark:text-rose-400" : "text-slate-400"
            }`}
            title={overdue ? "Overdue" : "Due date"}
          >
            <CalendarClock className="mr-0.5 inline h-3 w-3" />
            {task.dueDate}
          </span>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-1 pl-4">
        {task.caseRefs.map((c) => (
          <CaseBadge key={c} caseId={c} validCaseIds={validCaseIds} />
        ))}
      </div>
      {task.latestUpdate && (
        <div className="flex items-center gap-1 pl-4 text-xs text-slate-500 dark:text-slate-400">
          <AuthorIcon by={task.latestUpdate.by} />
          <span className="truncate">{task.latestUpdate.note}</span>
          <span className="shrink-0 text-slate-400">
            · {formatRelative(task.latestUpdate.at)}
          </span>
        </div>
      )}
    </button>
  );
}

// ─── Detail pane ────────────────────────────────────────────────────────────

function TaskDetailPane({
  task,
  detail,
  loading,
  error,
  onLoadOlder,
  loadingMore,
  validCaseIds,
  today,
}: {
  task: TaskRow | null;
  detail: TaskDetailResult | null;
  loading: boolean;
  error: string | null;
  onLoadOlder: () => void;
  loadingMore: boolean;
  validCaseIds: Set<string> | null;
  today: string;
}) {
  if (!task) {
    return (
      <div className="flex min-h-0 items-center justify-center rounded-md border border-dashed border-slate-200 p-6 text-sm text-slate-400 dark:border-slate-700">
        Select a task to see its detail + ledger.
      </div>
    );
  }
  const history = detail?.history ?? [];
  const total = detail?.historyTotal ?? task.updateCount;
  const hasMore = history.length < total;

  return (
    <div
      className="flex min-h-0 flex-col gap-3 overflow-y-auto rounded-md border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900"
      data-testid="tasklist-detail"
    >
      <header className="flex flex-col gap-1.5">
        <div className="flex items-center gap-2">
          <PriorityMark priority={task.priority} />
          <StatusChip status={task.status} />
          {task.audit.archivedAt && <Badge tone="slate">archived</Badge>}
          {task.createdBy === "agent" && <Badge tone="violet">agent-created</Badge>}
        </div>
        <h2 className="text-base font-semibold">{task.title}</h2>
        {task.detail && (
          <p className="whitespace-pre-wrap text-sm text-slate-600 dark:text-slate-300">
            {task.detail}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
          <span className="font-mono" title={task.id}>
            …{taskIdSuffix(task.id)}
          </span>
          <span>·</span>
          <span>created {new Date(task.audit.createdAt).toLocaleString()}</span>
          {task.dueDate && (
            <>
              <span>·</span>
              <span className={isOverdueForDisplay(task, today) ? "font-semibold text-rose-600 dark:text-rose-400" : ""}>
                due {task.dueDate}
              </span>
            </>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-1">
          {task.caseRefs.map((c) => (
            <CaseBadge key={c} caseId={c} validCaseIds={validCaseIds} />
          ))}
        </div>
        {task.recurrenceOf && (
          <p className="text-xs text-slate-500 dark:text-slate-400" data-testid="tasklist-recurrence">
            recurring — previous instance{" "}
            <span className="font-mono" title={task.recurrenceOf}>
              …{taskIdSuffix(task.recurrenceOf)}
            </span>
          </p>
        )}
      </header>

      <section className="flex flex-col gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {/* The count comes from the (stale) list row when `detail` is absent,
              so suppress it on error — a removed task must not advertise a
              ledger length above "this task no longer exists". */}
          Ledger{error ? "" : ` (${total})`}
        </h3>
        {loading && history.length === 0 ? (
          <div className="flex items-center gap-2 py-4 text-sm text-slate-400">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading ledger…
          </div>
        ) : error ? (
          <p className="rounded bg-rose-50 p-2 text-xs text-rose-700 dark:bg-rose-950 dark:text-rose-300">
            {error}
          </p>
        ) : (
          <>
            <ol className="flex flex-col gap-2" data-testid="tasklist-timeline">
              {history.map((u, i) => (
                <LedgerRow key={`${u.at}-${i}`} update={u} />
              ))}
            </ol>
            {hasMore && (
              <button
                type="button"
                onClick={onLoadOlder}
                disabled={loadingMore}
                className="self-start rounded border border-slate-200 px-2 py-1 text-xs text-indigo-600 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-indigo-400 dark:hover:bg-slate-800"
                data-testid="tasklist-load-older"
              >
                {loadingMore ? "Loading…" : `Load older (${total - history.length} more)`}
              </button>
            )}
          </>
        )}
      </section>
    </div>
  );
}

function LedgerRow({ update }: { update: UpdateRow }) {
  return (
    <li className="flex flex-col gap-0.5 border-l-2 border-slate-200 pl-2 dark:border-slate-700">
      <div className="flex items-center gap-1.5 text-xs">
        <AuthorIcon by={update.by} />
        <span className="font-medium text-slate-600 dark:text-slate-300">{update.by}</span>
        {update.statusChange && (
          <Badge tone="indigo">
            {update.statusChange.from} → {update.statusChange.to}
          </Badge>
        )}
        <span className="ml-auto text-slate-400" title={update.at}>
          {formatRelative(update.at)}
        </span>
      </div>
      <p className="whitespace-pre-wrap text-sm">{update.note}</p>
    </li>
  );
}

// ─── Small primitives ───────────────────────────────────────────────────────

function StatusChip({ status }: { status: TaskStatus }) {
  const cls =
    status === "open"
      ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200"
      : status === "waiting"
        ? "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200"
        : status === "done"
          ? "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200"
          : "bg-slate-100 text-slate-500 line-through dark:bg-slate-800 dark:text-slate-400";
  return (
    <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase ${cls}`}>
      {status}
    </span>
  );
}

/** `urgent` is rendered distinctly (handoff §2.1). */
function PriorityMark({ priority }: { priority: TaskPriority }) {
  if (priority === "urgent") {
    return (
      <span title="urgent" className="shrink-0 text-rose-600 dark:text-rose-400">
        <AlertTriangle className="h-3.5 w-3.5" />
      </span>
    );
  }
  const dot =
    priority === "high"
      ? "bg-orange-400"
      : priority === "normal"
        ? "bg-slate-300 dark:bg-slate-600"
        : "bg-slate-200 dark:bg-slate-700";
  return <span title={priority} className={`h-2 w-2 shrink-0 rounded-full ${dot}`} />;
}

function AuthorIcon({ by }: { by: "user" | "agent" }) {
  return by === "agent" ? (
    <Bot className="h-3 w-3 shrink-0 text-violet-500" aria-label="agent" />
  ) : (
    <UserIcon className="h-3 w-3 shrink-0 text-slate-400" aria-label="user" />
  );
}

/** A caseRef that the org registry can't resolve is shown as archived/unknown
 *  (handoff §2.1). When the registry read failed we don't badge at all. */
function CaseBadge({
  caseId,
  validCaseIds,
}: {
  caseId: string;
  validCaseIds: Set<string> | null;
}) {
  const unresolved = validCaseIds !== null && !validCaseIds.has(caseId);
  return (
    <span
      className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-mono text-[10px] ${
        unresolved
          ? "bg-slate-100 text-slate-400 line-through dark:bg-slate-800 dark:text-slate-500"
          : "bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-200"
      }`}
      title={unresolved ? `${caseId} — archived or no longer in the registry` : caseId}
    >
      {caseId}
      {unresolved && <span className="not-italic">archived</span>}
    </span>
  );
}

function Badge({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone: "slate" | "violet" | "indigo";
}) {
  const cls =
    tone === "violet"
      ? "bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-200"
      : tone === "indigo"
        ? "bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200"
        : "bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300";
  return (
    <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium ${cls}`}>
      {children}
    </span>
  );
}
